import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import {
  formatABN,
  formatMobile,
  formatName,
  formatPhone,
} from "../../lib/Misc";
import { debounce } from "lodash";
import MainBody from "../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../components/Pagination";
import sortCaret from "../../components/SortCaret";
// import SyncXero from "../../components/SyncXero";
import UploadSupplier from "./UploadSupplier";
import AddEditSupplier from "./AddEditSupplier";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import SupplierActionButton from "./SupplierActionButton";
import Confirmation from "../../components/Confirmation";
import { useTrust } from "../../lib/context/TrustContext";

const Suppliers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [suppliersList, setSuppliersList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { trustId } = useTrust();
  // const builderId = localStorage.getItem("builderId")
  //   ? localStorage.getItem("builderId")
  //   : "";

  const [selectedRowIds, setSelectedRowIds] = useState([]);

  const [supplierId, setSupplierId] = useState(null);
  const [showAddEditSupplier, setShowAddEditSupplier] = useState(false);
  const [showSupplierDelete, setShowSupplierDelete] = useState(false);

  const toggleShowAddEditSupplier = () => {
    if (showAddEditSupplier) {
      setEdit(false);
      setSupplierId(null);
    }
    setShowAddEditSupplier(!showAddEditSupplier);
  };

  const toggleShowDelete = () => {
    if (showSupplierDelete) {
      setSupplierId(null);
    }
    setShowSupplierDelete(!showSupplierDelete);
  };

  const fetchSupplier = useCallback(
    async (passLimit, passPage) => {
      if (trustId) {
        try {
          setLoading(true);
          const { data } = await get(
            `${API.GET_SUPPLIERS}?limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&search=${
              searchParam ? searchParam : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }&trust_id=${trustId ? trustId : ""}`
          );

          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          const suppliersList = data.data.data;
          let notSelectedIds = [];
          for (let i = 0; i < suppliersList?.length; i++) {
            notSelectedIds.push(suppliersList[i]?.id);
          }
          // setNotSelectedRowIds(notSelectedIds);
          setSuppliersList(suppliersList);
          return data.data;
        } catch (error) {
          setLoading(false);
          setSuppliersList([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, trustId]
  );

  const suppliersGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: suppliersList[i]?.id,
        code: suppliersList[i]?.code ? suppliersList[i]?.code : "-",
        name: formatName(
          suppliersList[i]?.first_name,
          suppliersList[i]?.last_name
        ),
        email: suppliersList[i]?.email,
        abn: suppliersList[i]?.abn ? formatABN(suppliersList[i]?.abn) : "-",
        phone: suppliersList[i]?.phone
          ? formatPhone(suppliersList[i]?.phone)
          : "-",
        mobile: suppliersList[i]?.mobile
          ? formatMobile(suppliersList[i]?.mobile)
          : "-",
        business_name: suppliersList[i]?.business_name
          ? suppliersList[i]?.business_name
          : "-",
        source: suppliersList[i]?.import_from
          ? suppliersList[i]?.import_from
          : "TrustSoft",
        action: (
          <>
            <SupplierActionButton
              id={suppliersList[i]?.id}
              setEdit={setEdit}
              used={suppliersList[i]?.is_used ? true : false}
              setSupplierId={setSupplierId}
              toggleShowAddEditSupplier={toggleShowAddEditSupplier}
              toggleShowDelete={toggleShowDelete}
            />
          </>
        ),
      });
    }
    return items;
  };

  const suppliers = suppliersGenerator(suppliersList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "code",
      text: "Code",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "name",
      text: "Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "business_name",
      text: "Business Name",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "email",
      text: "Email",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "abn",
      text: "ABN",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "phone",
      text: "Phone No.",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "mobile",
      text: "Mobile No.",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "source",
      text: "Source",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchSupplier();
  }, [fetchSupplier]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchSupplier(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchSupplier(limit, page);
  };

  const arrayRemove = (arr, value) => {
    return arr.filter(function (ele) {
      return ele !== value;
    });
  };

  const mainSelectRow = {
    mode: "checkbox",
    clickToSelect: false,
    classes: TDClasses,
    selected: selectedRowIds,
    // nonSelectable: xeroData?.connection_status === 1 ? [] : notSelectedRowIds,
    onSelectAll: (row, isSelect) => {
      if (suppliersList?.length > 0) {
        if (row) {
          const ids =
            isSelect &&
            isSelect?.length > 0 &&
            isSelect?.map((item) => {
              return item?.key;
            });
          setSelectedRowIds(selectedRowIds ? selectedRowIds.concat(ids) : ids);
        } else {
          const removableData =
            isSelect &&
            isSelect?.length > 0 &&
            isSelect.map(function (data) {
              return data?.key;
            });
          setSelectedRowIds(
            selectedRowIds
              ? selectedRowIds.filter((item) => !removableData.includes(item))
              : []
          );
        }
      }
    },
    onSelect: (row, isSelect) => {
      if (isSelect) {
        setSelectedRowIds((keys) => [...keys, row?.key]);
      } else {
        setSelectedRowIds(arrayRemove(selectedRowIds, row?.key));
      }
    },
  };

  // Single Supplier Delete
  const handleSupplierDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.SUPPLIER_DELETE}/${supplierId}`);
      setLoading(false);
      fetchSupplier(limit, page);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Sync Xero */}
            {/* <SyncXero
              builderId={builderId}
              selectedRow={selectedRowIds}
              setSelectedRow={setSelectedRowIds}
              trustId={trustId}
              fetchData={() => fetchSupplier(limit, page)}
              recordId={supplierId}
              setRecordId={setSupplierId}
              module="supplier_ids"
              api={API.CONTACT_SYNC}
            /> */}

            {/* Import Supplier */}
            <UploadSupplier
              fetchData={() => fetchSupplier(limit, page)}
              trustId={trustId}
              ButtonName={"Import"}
              setLoading={setLoading}
            />

            {/* Add Supplier */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Supplier" : "Add Supplier"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setSupplierId(null);
                setShowAddEditSupplier(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Supplier</span>
                </div>
              }
              className={""}
              open={showAddEditSupplier}
              buttonClassName={""}
              setOpen={setShowAddEditSupplier}
              write={true}
            >
              <AddEditSupplier
                edit={edit}
                toggleShowAddEditSupplier={toggleShowAddEditSupplier}
                id={supplierId}
                trustId={trustId}
                fetchSupplierData={() => fetchSupplier(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        mainClassName="w-[calc(100%-3rem)]"
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={suppliers}
              selectRow={mainSelectRow}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Supplier"}
        open={showSupplierDelete}
        setOpen={setShowSupplierDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete supplier?"}
        okAction={async () => {
          handleSupplierDelete();
          toggleShowDelete();
          // fetchSupplier();
        }}
        cancelAction={() => {
          setSupplierId(null);
          setShowSupplierDelete(!showSupplierDelete);
        }}
      />
    </>
  );
};

export default Suppliers;
